import { useState, useEffect } from 'react';
import axios from 'axios';
import { useFormContext } from 'react-hook-form';
import { QueryKeyConfig, getData } from '../api/api-helper';

interface useDebouncedValidationProps {
  name: string;
  validationQueryRoute?: (value: any) => QueryKeyConfig;
  debounceDelay?: number;
}

const useDebouncedValidation = ({ name, validationQueryRoute, debounceDelay = 300 }: useDebouncedValidationProps) => {
  const [value, setValue] = useState<any>();
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    setError: setFormError,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();

    const fetchData = async () => {
      try {
        const query = validationQueryRoute?.(value);
        if (value?.trim() !== '' && query && query.isEnabled !== false) {
          setIsLoading(true);
          const response = await getData<any>(query.route, query.params, { cancelToken: source.token });

          if (response.message && response.message !== '') {
            setError(response.data.error);
            setFormError(name, { message: response.message });
          } else {
            setError(null);

            setFormError(name, { message: undefined });

            if (errors[name] && errors[name]?.type === 'manual') {
              setFormError(name, { message: undefined });
            }
          }
        }
      } catch (error: any) {
        if (!axios.isCancel(error)) {
          const errorMessage: any = error?.response?.data.message;

          console.error('Error validating:', error);
          setError(errorMessage ? errorMessage : 'Error occurred while validating');
          setFormError(name, { message: errorMessage ? errorMessage : 'Error occurred while validating' });
        }
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, debounceDelay);

    return () => {
      clearTimeout(debounceTimer);
      source.cancel('Request canceled due to new text change');
    };
  }, [value, debounceDelay, setFormError]);

  const validate = (newValue: any) => {
    setValue(newValue);
    setError(null);
    if (newValue == '') setFormError(name, { message: undefined });
  };

  return { value, validate, error, isLoading };
};

export default useDebouncedValidation;
